import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import StatsCard from '../../components/feature/StatsCard';
import LazyImage from '../../components/common/LazyImage';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { walletManager } from '../../utils/walletManager';
import SimpleWalletConnect from '../../components/wallet/SimpleWalletConnect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faCheckCircle, faWallet, faEye, faTimesCircle, faArrowRight, faCode, faDownload, faUserCog, faCodeBranch, faLink, faUserShield, faLock, faExchangeAlt, faUserTimes, faEyeSlash, faUsers, faUserSecret
} from '@fortawesome/free-solid-svg-icons';

const HomePage = () => {
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletInfo, setWalletInfo] = useState<any>(null);

  useEffect(() => {
    // Check initial connection status
    checkWalletStatus();

    // Listen for wallet status change events
    const handleWalletStatusChange = (event: CustomEvent) => {
      const { isConnected, walletInfo } = event.detail;
      setIsWalletConnected(isConnected);
      setWalletInfo(walletInfo);
    };

    window.addEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);

    return () => {
      window.removeEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);
    };
  }, []);

  const checkWalletStatus = async () => {
    const connected = await walletManager.isConnected();
    const info = walletManager.getWalletInfo();
    setIsWalletConnected(connected);
    setWalletInfo(info);
  };

  const handleConnectWallet = async () => {
    console.log('Connect wallet button clicked');
    if (isWalletConnected) {
      // If already connected, navigate to wallet page or show connected status
      console.log('Wallet already connected');
      return;
    }
    
    console.log('Attempting to connect wallet...');
    const result = await walletManager.connectWallet();
    console.log('Wallet connection result:', result);
    
    if (result.success) {
      setIsWalletConnected(true);
      setWalletInfo(result.walletInfo);
      console.log('Wallet connected successfully');
    } else {
      console.error('Wallet connection failed:', result.error);
      alert(`Failed to connect wallet: ${result.error}`);
    }
  };

  const features = [
    {
      title: "ZKP",
      description: "Prove data authenticity without revealing sensitive information",
      icon: <FontAwesomeIcon icon={faEyeSlash} className="text-teal-600 text-3xl" />,
      color: "text-teal-600"
    },
    {
      title: "Homomorphic Encryption",
      description: "Perform computations directly on encrypted data",
      icon: <FontAwesomeIcon icon={faLock} className="text-blue-600 text-3xl" />,
      color: "text-blue-600"
    },
    {
      title: "Data Sovereignty",
      description: "Users maintain complete control over their data and identity",
      icon: <FontAwesomeIcon icon={faUserShield} className="text-purple-600 text-3xl" />,
      color: "text-purple-600"
    },
    {
      title: "Cross-Chain Interop",
      description: "Seamless integration with mainstream blockchain networks",
      icon: <FontAwesomeIcon icon={faCodeBranch} className="text-green-600 text-3xl" />,
      color: "text-green-600"
    }
  ];

  const stats = [
    {
      label: "Privacy-Protected Transactions Processed",
      value: "10M+",
      description: "Privacy-protected transactions processed"
    },
    { 
      label: "Data Value Flowing Through Protocol", 
      value: "US$5B", 
      description: "Data value flowing through protocol" 
    },
    { 
      label: "Privacy Protection Success Rate", 
      value: "99.9%", 
      description: "Privacy protection success rate" 
    },
    { 
      label: "Supported Blockchain Networks", 
      value: "15+", 
      description: "Supported blockchain networks" 
    }
  ];

  const privacyValues = ["Data Sovereignty", "Privacy Protection", "Verifiable Computing", "Decentralization", "Cross-Chain Interop", "Zero-Knowledge Proofs", "User Control"];

  const traditionalValues = ["Data Monopoly", "Privacy Leaks", "Black Box Computing", "Centralization", "Isolated Systems", "Complete Transparency", "Platform Control"];

  const useCases = [
    {
      title: "Privacy-Preserving DeFi",
      description: "Providing decentralized financial services while protecting user privacy and preventing transaction information leakage.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGcxKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGcxIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzBkYTVlOSIvPjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjMTBhOWI3Ii8+PHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjMTRiOGE2Ii8+PC9saW5lYXJHcmFkaWVudD48L2RlZnM+PGNpcmNsZSBjeD0iMTgwIiBjeT0iMTAwIiByPSIxOCIgZmlsbD0id2hpdGUiIG9wYWNpdHk9IjAuOTUiLz48cGF0aCBkPSJNMjAwIDkwQzIyMCA4OCAzMzAgMTAwIDMwMCAxMzBDMjgwIDE0MCAxODAgMTMwIDIwMCAxMDBMNTUgMTAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuNyIvPjxwYXRoIGQ9Ik0yMDAgMjEwQzIwMCAxNjAgMjYwIDE1MCAzMDAgMjAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuOCIvPjxjaXJjbGUgY3g9IjE1MCIgY3k9IjE4MCIgcj0iMjAiIGZpbGw9IndoaXRlIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyNTAiIGN5PSIxODAiIHI9IjIwIiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC45Ii8+PHBhdGggZD0iTTE1MCAxNjBMMjUwIDE2MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIi8+PC9zdmc+",
      color: "from-teal-500/10 to-teal-500/5 border-teal-500/10",
      link: "/privacy-defi"
    },
    {
      title: "Data Marketplace",
      description: "Building secure data trading markets where data providers can earn revenue without exposing raw data.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGcyKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGcyIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzFkNjlmYiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzNkYTdjNCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjE2MCIgY3k9IjEzMCIgcj0iMzAiIGZpbGw9InVybCgjbGcyKSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyNDAiIGN5PSIxMzAiIHI9IjMwIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNzAiIHI9IjMwIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIxNDAiIGN5PSIyMDAiIHI9IjE1IiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC44Ii8+PGNpcmNsZSBjeD0iMjYwIiBjeT0iMjAwIiByPSIxNSIgZmlsbD0id2hpdGUiIG9wYWNpdHk9IjAuOCIvPjxwYXRoIGQ9Ik0xNjAgMTM1TDI0MCAxMzUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PHBhdGggZD0iTTE3MCAxNDBMMjAwIDE3MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI0IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48cGF0aCBkPSJNMjMwIDE0MEwyMDAgMTcwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjwvc3ZnPg==",
      color: "from-blue-500/10 to-blue-500/5 border-blue-500/10",
      link: "/data-marketplace"
    },
    {
      title: "Identity Verification",
      description: "Providing decentralized identity verification services where users can prove identity without revealing personal information.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGczKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGczIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI2IyMDBiYiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzljNTYzNyIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjIwMCIgY3k9IjEwMCIgcj0iNTUiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgb3BhY2l0eT0iMC45Ii8+PGNpcmNsZSBjeD0iMjAwIiBjeT0iODAiIHI9IjI1IiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC44Ii8+PHBhdGggZD0iTTE0NSAxNTBMMTgwIDE1MEwyMDUgMTY1TDIzMCAxNTBMNTAgMTUwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuODUiLz48L3N2Zz4=",
      color: "from-purple-500/10 to-purple-500/5 border-purple-500/10",
      link: "/identity-verification"
    },
    {
      title: "Supply Chain Privacy",
      description: "Protecting business secrets in supply chain management while ensuring data verifiability and transparency.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGc0KSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGc0IiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzEwYjk4MSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzIyYzU3MCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjEwMCIgY3k9IjEwMCIgcj0iMjQiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iMjAwIiBjeT0iMTAwIiByPSIyNCIgZmlsbD0id2hpdGUiLz48Y2lyY2xlIGN4PSIzMDAiIGN5PSIxMDAiIHI9IjI0IiBmaWxsPSJ3aGl0ZSIvPjxjaXJjbGUgY3g9IjE1MCIgY3k9IjIwMCIgcj0iMjQiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iMjUwIiBjeT0iMjAwIiByPSIyNCIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTIwIDEwMEwyNzAgMTAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjxwYXRoIGQ9Ik0xNjAgMTIwTDE2MCAxODAiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PHBhdGggZD0iTTI0MCAxMjBMMjQwIDE4MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI1IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48L3N2Zz4=",
      color: "from-green-500/10 to-green-500/5 border-green-500/10",
      link: "/supply-chain-privacy"
    }
  ];

  const techLayers = [
    {
      title: "🧠 Privacy Protection Layer",
      description: "Using zero-knowledge proofs and homomorphic encryption to ensure data remains confidential during computation and verification, achieving 'verifiable yet invisible data'.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGcxKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGcxIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzBkYTVlOSIvPjxzdG9wIG9mZnNldD0iNTAlIiBzdG9wLWNvbG9yPSIjMTBhOWI3Ii8+PHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjMTRiOGE2Ii8+PC9saW5lYXJHcmFkaWVudD48L2RlZnM+PGNpcmNsZSBjeD0iMTgwIiBjeT0iMTAwIiByPSIxOCIgZmlsbD0id2hpdGUiIG9wYWNpdHk9IjAuOTUiLz48cGF0aCBkPSJNMjAwIDkwQzIyMCA4OCAzMzAgMTAwIDMwMCAxMzBDMjgwIDE0MCAxODAgMTMwIDIwMCAxMDBMNTUgMTAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjMiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuNyIvPjxwYXRoIGQ9Ik0yMDAgMjEwQzIwMCAxNjAgMjYwIDE1MCAzMDAgMjAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuOCIvPjxjaXJjbGUgY3g9IjE1MCIgY3k9IjE4MCIgcj0iMjAiIGZpbGw9IndoaXRlIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyNTAiIGN5PSIxODAiIHI9IjIwIiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC45Ii8+PHBhdGggZD0iTTE1MCAxNjBMMjUwIDE2MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIi8+PC9zdmc+",
      color: "from-teal-500/10 to-teal-500/5 border-teal-500/10",
      icon: faEyeSlash,
      link: "/privacy-layer"
    },
    {
      title: "🔒 Identity Layer",
      description: "Providing decentralized identity verification services where users can prove identity without revealing personal information.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGczKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGczIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iI2IyMDBiYiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzljNTYzNyIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjIwMCIgY3k9IjEwMCIgcj0iNTUiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgb3BhY2l0eT0iMC45Ii8+PGNpcmNsZSBjeD0iMjAwIiBjeT0iODAiIHI9IjI1IiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC44Ii8+PHBhdGggZD0iTTE0NSAxNTBMMTgwIDE1MEwyMDUgMTY1TDIzMCAxNTBMNTAgMTUwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiIG9wYWNpdHk9IjAuODUiLz48L3N2Zz4=",
      color: "from-purple-500/10 to-purple-500/5 border-purple-500/10",
      icon: faUserShield,
      link: "/identity-layer"
    },
    {
      title: "💰 Economy Layer",
      description: "Building secure data trading markets where data providers can earn revenue without exposing raw data.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGcyKSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGcyIiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzFkNjlmYiIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzNkYTdjNCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjE2MCIgY3k9IjEzMCIgcj0iMzAiIGZpbGw9InVybCgjbGcyKSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyNDAiIGN5PSIxMzAiIHI9IjMwIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIyMDAiIGN5PSIxNzAiIHI9IjMwIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIiBvcGFjaXR5PSIwLjkiLz48Y2lyY2xlIGN4PSIxNDAiIGN5PSIyMDAiIHI9IjE1IiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC44Ii8+PGNpcmNsZSBjeD0iMjYwIiBjeT0iMjAwIiByPSIxNSIgZmlsbD0id2hpdGUiIG9wYWNpdHk9IjAuOCIvPjxwYXRoIGQ9Ik0xNjAgMTM1TDI0MCAxMzUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PHBhdGggZD0iTTE3MCAxNDBMMjAwIDE3MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI0IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48cGF0aCBkPSJNMjMwIDE0MEwyMDAgMTcwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjQiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjwvc3ZnPg==",
      color: "from-blue-500/10 to-blue-500/5 border-blue-500/10",
      icon: faExchangeAlt,
      link: "/economy-layer"
    },
    {
      title: "🌐 Interop Layer",
      description: "Seamless integration with mainstream blockchain networks, enabling cross-chain data sharing and interoperability.",
      image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjbGc0KSIvPjxkZWZzPjxsaW5lYXJHcmFkaWVudCBpZD0ibGc0IiB4MT0iMCIgeTE9IjAiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzEwYjk4MSIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzIyYzU3MCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjxjaXJjbGUgY3g9IjEwMCIgY3k9IjEwMCIgcj0iMjQiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iMjAwIiBjeT0iMTAwIiByPSIyNCIgZmlsbD0id2hpdGUiLz48Y2lyY2xlIGN4PSIzMDAiIGN5PSIxMDAiIHI9IjI0IiBmaWxsPSJ3aGl0ZSIvPjxjaXJjbGUgY3g9IjE1MCIgY3k9IjIwMCIgcj0iMjQiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iMjUwIiBjeT0iMjAwIiByPSIyNCIgZmlsbD0id2hpdGUiLz48cGF0aCBkPSJNMTIwIDEwMEwyNzAgMTAwIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPjxwYXRoIGQ9Ik0xNjAgMTIwTDE2MCAxODAiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+PHBhdGggZD0iTTI0MCAxMjBMMjQwIDE4MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSI1IiBzdHJva2UtbGluZWNhcD0icm91bmQiLz48L3N2Zz4=",
      color: "from-green-500/10 to-green-500/5 border-green-500/10",
      icon: faLink,
      link: "/interop-layer"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden overflow-x-hidden">
        {/* Announcement Banner */}
        <div className="relative bg-gradient-to-r from-teal-400 to-blue-600 text-white">
          <div className="absolute inset-0 flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <FontAwesomeIcon icon={faShieldAlt} className="text-white text-xl" />
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-bold">Titron Mainnet</p>
                <p className="text-sm">Coming Soon</p>
              </div>
            </div>
            <div className="text-center flex-1">
              <p className="text-sm font-bold">Mainnet Coming Soon · Join Testnet</p>
            </div>
            <Link
              to="/community"
              className="bg-teal-700 hover:bg-teal-800 px-4 py-2 rounded text-sm font-bold transition-colors"
            >
              Join Testnet
            </Link>
          </div>
        </div>

        {/* Main Hero */}
        <div className="relative">
          <div className="h-80 md:h-96 lg:h-[480px] overflow-hidden">
            <LazyImage
              src="/images-optimized/1-Titron.webp"
              alt="Titron Ecosystem"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative bg-white border-t-4 border-teal-500">
          <div className="max-w-7xl mx-auto px-4 py-16 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-teal-50 text-teal-600 rounded-full text-sm font-medium mb-6">
              Titron Protocol
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 md:mb-6 px-2 break-words">
              Building the Privacy Foundation for Web3
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-6 md:mb-8 px-4 break-words">
              Through zero-knowledge proofs and homomorphic encryption, enabling secure data sharing while maintaining verifiability, achieving true data sovereignty
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/developers"
                className="px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-lg"
              >
                Start Building
              </Link>
              {isWalletConnected ? (
                <button
                  className="px-8 py-4 bg-gray-400 text-white rounded-lg cursor-default font-medium text-lg flex items-center justify-center gap-2"
                  disabled
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Successfully Connected
                </button>
              ) : (
                <Link
                  to="/wallet-connect"
                  className="px-8 py-4 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium text-lg flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faWallet} />
                  Connect Wallet
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((feature, index) => {
            const featureLinks = ['/privacy-layer', '/identity-layer', '/economy-layer', '/interop-layer'];
            return (
            <Link
              key={index}
              to={featureLinks[index] || '#'}
              className="group p-6 bg-white rounded-2xl border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div
                  className={`w-16 h-16 rounded-2xl border border-gray-200 flex items-center justify-center group-hover:border-teal-300 transition-colors`}
                >
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-tr from-${feature.color.split('-')[1].replace('00', '500')}/10 to-${feature.color.split('-')[1].replace('00', '500')}/5`}>
                    {feature.icon}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{feature.description}</p>
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      </section>

      {/* Core Problem */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-1 bg-red-50 text-red-600 rounded-full text-sm font-medium uppercase mb-4">
            Core Problem
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
            Current Blockchain Challenges
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Transparent blockchain technology exposes user data and transaction privacy, users lose control over their own data, and cannot monetize data value
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-8 bg-red-50 rounded-2xl border border-red-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon icon={faEye} className="text-red-600 text-xl" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">Privacy Leaks</h3>
            </div>
            <p className="text-gray-700 mb-4">
              The transparent nature of blockchain exposes all transactions and data to public view, completely revealing users' financial status, trading habits, and other private information.
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-lg" />
                <span>Transaction amounts and addresses are public</span>
              </li>
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-lg" />
                <span>User behavior patterns are traceable</span>
              </li>
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-lg" />
                <span>Business secrets cannot be protected</span>
              </li>
            </ul>
          </div>

          <div className="p-8 bg-orange-50 rounded-2xl border border-orange-200">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon icon={faUserCog} className="text-orange-600 text-xl" />
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white break-words">Data Loss of Control</h3>
            </div>
            <p className="text-gray-700 mb-4">
              Users lose control over their own data, unable to decide how data is used, and cannot benefit from data value.
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-orange-500 text-lg" />
                <span>Data monopolized by platforms</span>
              </li>
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-orange-500 text-lg" />
                <span>Unable to control data usage</span>
              </li>
              <li className="flex items-center gap-2">
                <FontAwesomeIcon icon={faTimesCircle} className="text-orange-500 text-lg" />
                <span>Data value cannot be monetized</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technology Layers */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-1 bg-teal-50 text-teal-600 rounded-full text-sm font-medium uppercase mb-4">
            Technical Architecture
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
            Four Technical Layers
          </h2>
          <p className="text-lg text-gray-600">Building a complete privacy protection technology stack</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {techLayers.map((layer, index) => (
            <div
              key={index}
              className={`group rounded-2xl p-8 bg-gradient-to-br ${layer.color} border hover:shadow-lg transition-all duration-200`}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-white/80 rounded-lg flex items-center justify-center">
                      <FontAwesomeIcon icon={layer.icon} className="text-gray-700 text-xl" />
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-black text-gray-900 dark:text-white group-hover:underline break-words">
                      {layer.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-6">{layer.description}</p>
                  <Link
                    to={layer.link}
                    className="inline-flex items-center px-4 py-2 border border-gray-900 text-gray-900 rounded hover:bg-gray-900 hover:text-white transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
                <div className="w-full lg:w-80 h-48 rounded-xl overflow-hidden shadow-xl group-hover:scale-105 transition-transform duration-300">
                  <LazyImage
                    src={layer.image}
                    alt={layer.title}
                    className="w-full h-full object-cover brightness-95 group-hover:brightness-110 transition-all duration-300"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium uppercase mb-4">
            Applications
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
            Revolutionary Privacy-Preserving Applications
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className={`group rounded-2xl p-8 bg-gradient-to-br ${useCase.color} border hover:shadow-lg transition-all duration-200`}
            >
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:underline">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-700 mb-6">{useCase.description}</p>
                  <Link
                    to={useCase.link}
                    className="inline-flex items-center px-4 py-2 border border-gray-900 text-gray-900 rounded hover:bg-gray-900 hover:text-white transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
                <div className="w-full lg:w-80 h-48 rounded-xl overflow-hidden">
                  <LazyImage
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row gap-16">
          <div className="lg:w-1/2">
            <div className="w-full h-80 lg:h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-teal-500/10 to-teal-500/5">
              <LazyImage
                src="/images-optimized/03-Titron.webp"
                alt="PrivacyChain Network Activity"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:w-1/2">
            <div className="inline-flex items-center px-4 py-1 bg-teal-50 text-teal-600 rounded-full text-sm font-medium uppercase mb-4">
              Network Activity
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-6 md:mb-8 break-words px-2">
              Privacy Protection Ecosystem
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-6 md:mb-8 break-words px-2">
              All activity on the Titron network
            </p>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {stats.map((stat, index) => (
                <StatsCard
                  key={index}
                  label={stat.label}
                  value={stat.value}
                  description={stat.description}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/developer-ecosystem"
                className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                Developer Ecosystem
              </Link>
              <Link
                to="/network-stats"
                className="px-6 py-3 border border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium"
              >
                Network Statistics
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values Comparison */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-1 bg-teal-50 text-teal-600 rounded-full text-sm font-medium uppercase mb-4">
            Value Comparison
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
            Web3 is Undergoing Transformation
          </h2>
          <p className="text-lg text-gray-600">From data monopoly to data sovereignty</p>
        </div>

        <div className="relative overflow-hidden rounded-2xl border">
          {/* PrivacyChain Values */}
          <div className="bg-teal-50 border-b">
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll-left space-x-4 px-6 py-8">
                {[...privacyValues, ...privacyValues].map((value, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="bg-teal-100 text-teal-600 px-4 py-2 rounded-full text-sm font-bold uppercase whitespace-nowrap hover:bg-teal-600 hover:text-white transition-colors cursor-pointer">
                      {value}
                    </div>
                    {index < privacyValues.length * 2 - 1 && (
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Traditional Values */}
          <div className="bg-gray-50">
            <div className="flex overflow-hidden">
              <div className="flex animate-scroll-right space-x-4 px-6 py-8">
                {[...traditionalValues, ...traditionalValues].map((value, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm font-bold uppercase whitespace-nowrap hover:bg-gray-600 hover:text-white transition-colors cursor-pointer">
                      {value}
                    </div>
                    {index < traditionalValues.length * 2 - 1 && (
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center Label */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center bg-white rounded-lg shadow-lg">
            <div className="bg-gray-50 px-4 py-2 text-gray-600 text-sm font-bold">
              Traditional Model
            </div>
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-50"></div>
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-teal-50"></div>
            <div className="bg-teal-50 px-4 py-2 text-teal-600 text-sm font-bold">
              Titron
            </div>
          </div>
        </div>
      </section>

      {/* Learn Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row-reverse gap-16">
          <div className="lg:w-1/2">
            <div className="w-full h-80 lg:h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-blue-500/10 to-blue-500/5">
              <LazyImage
                src="/images-optimized/06-Titron.webp"
                alt="Learn Privacy Protection Technology"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:w-1/2">
            <div className="inline-flex items-center px-4 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium uppercase mb-4">
              Learning
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-6 md:mb-8 break-words px-2">
              Understanding Privacy Protection Technology
            </h2>
            <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-6 md:mb-8 break-words px-2">
              Privacy protection technology might seem complex. Don't worry, these materials will help you understand Titron's core concepts in minutes.
            </p>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Popular Topics</h3>
                <div className="space-y-3">
                  <Link to="/zkp-guide" className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-1">What are Zero-Knowledge Proofs?</h4>
                    <p className="text-sm text-gray-600">Understanding the basic concepts and applications of zero-knowledge proofs</p>
                  </Link>
                  <Link to="/privacy-protection-guide" className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-1">How to Protect Data Privacy?</h4>
                    <p className="text-sm text-gray-600">Learning best practices for data privacy protection</p>
                  </Link>
                  <Link to="/data-sovereignty-guide" className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-1">Data Sovereignty Beginner's Guide</h4>
                    <p className="text-sm text-gray-600">Mastering the core concepts of data sovereignty</p>
                  </Link>
                  <Link to="/whitepaper" className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-1">Titron Whitepaper</h4>
                    <p className="text-sm text-gray-600">In-depth understanding of Titron's technical architecture</p>
                  </Link>
                </div>
              </div>
              <Link
                to="/more-learning-resources"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                More Learning Resources
                <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-lg" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Developers Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-1 bg-green-50 text-green-600 rounded-full text-sm font-medium uppercase mb-4">
              Developers
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
              Privacy Protection Developer Community
            </h2>
            <p className="text-lg text-gray-600">
              Titron is the most active developer ecosystem in Web3 privacy protection. Use our SDKs and tools to easily build privacy-preserving applications.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faCode} className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Developer Documentation</h3>
                    <p className="text-gray-700">Complete API documentation and development guides for quick start in privacy-preserving application development</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={faDownload} className="text-white text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">SDK Download</h3>
                    <p className="text-gray-700">SDKs supporting multiple programming languages, simplifying privacy protection feature integration</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link
                  to="/docs"
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Developer Documentation
                </Link>
                <Link
                  to="/sdk"
                  className="px-6 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors font-medium"
                >
                  SDK Download
                </Link>
              </div>
            </div>
            <div>
              <div className="bg-gray-50 rounded-3xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Code Examples</h3>
                <div className="space-y-4">
                  <Link to="/privacy-voting" className="block bg-white rounded-lg p-4 border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-2">Privacy-Preserving Voting</h4>
                    <p className="text-sm text-gray-600">Building anonymous voting systems</p>
                  </Link>
                  <Link to="/zk-identity-verification" className="block bg-white rounded-lg p-4 border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-2">Zero-Knowledge Identity Verification</h4>
                    <p className="text-sm text-gray-600">Verify identity without revealing information</p>
                  </Link>
                  <Link to="/encrypted-data-transactions" className="block bg-white rounded-lg p-4 border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all">
                    <h4 className="font-bold text-gray-900 mb-2">Encrypted Data Transactions</h4>
                    <p className="text-sm text-gray-600">Secure data marketplace transactions</p>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-1 bg-purple-50 text-purple-600 rounded-full text-sm font-medium uppercase mb-4">
            COMMUNITY
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black text-gray-900 dark:text-white mb-4 break-words px-2">
            TITRON COMMUNITY
          </h2>
          <p className="text-lg text-gray-600">Built by the community</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="space-y-6 text-lg text-gray-700">
              <p>
                Titron is built by global privacy protection technology enthusiasts, developers, researchers, and users, dedicated to advancing Web3 privacy protection technology.
              </p>
              <p>
                Welcome to join our community, exchange ideas with privacy protection technology experts worldwide, participate in protocol development, and build a more private and secure Web3 future together.
              </p>
              <p>
                The Titron community is the best starting point for learning privacy protection technology and participating in the Web3 privacy revolution.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/about-titron"
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                About Titron
              </Link>
              <Link
                to="/community"
                className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                Discord
              </Link>
              <Link
                to="/community"
                className="px-6 py-3 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium"
              >
                GitHub
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="w-full h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-purple-500/10 to-purple-500/5">
              <LazyImage
                src="/images-optimized/05-Titron.webp"
                alt="Titron Community"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Join Titron */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-br from-teal-500 to-blue-600 rounded-3xl p-12 text-center text-white">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 md:mb-6 break-words px-2">
            Join Titron
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 text-white/90 max-w-2xl mx-auto break-words px-4">
            Participate in building the future of Web3 privacy protection, returning data sovereignty to users
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-2">Developer Contribution</h3>
              <p className="text-sm text-white/80">Participate in protocol development, building privacy protection tools and applications.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-2">GitHub</h3>
              <p className="text-sm text-white/80">View source code, submit issues and improvement suggestions</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-2">Discord</h3>
              <p className="text-sm text-white/80">Join community discussions, get technical support and latest updates.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-2">Research Collaboration</h3>
              <p className="text-sm text-white/80">Participate in privacy protection technology research, advancing academic development.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/developers"
              className="px-8 py-4 bg-white text-teal-600 rounded-lg hover:bg-gray-100 transition-colors font-bold text-lg"
            >
              Developer Documentation
            </Link>
            <Link
              to="/community"
              className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white/10 transition-colors font-bold text-lg"
            >
              Join Community
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;